import { useEffect, useRef } from "react"
import { useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { CaretDown, Plus } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { ScrollArea } from "@/components/ui/scroll-area"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { cn } from "@/lib/utils"
import { EpisodeOfCareSofiaSuggestions } from "@/modules/patients/detail/episode-of-care/episode-of-care-ai-suggestions"
import { getEpisodeOfCareFragment } from "@/modules/patients/detail/episode-of-care/lib/get-episode-of-care-fragment"
import {
  EpisodeOfCareFragment$data,
  EpisodeOfCareFragment$key,
} from "@/modules/patients/detail/episode-of-care/types/episode-of-care-fragment-type"
import { AssignEpisodeOfCareFormType } from "@/modules/patients/detail/progress-note/progress-note-card-header"
import { useEpisodeOfCareStoreContext } from "@/modules/patients/detail/store/useEpisodeOfCareStore"
import { useAppointmentParam } from "@/modules/patients/hooks/useAppointmentParam"
import { usePatientEpisodesOfCareData } from "@/modules/patients/hooks/usePatientEpisodesOfCareData"
import { formatEpisodeOfCareDuration } from "@/modules/patients/libs/format-episode-of-care-duration"
import { Country } from "@/modules/patients/services/__generated__/PatientFragment.graphql"
import {
  CommunicationChannel,
  ProgressNoteAppointmentFragment$data,
  RequestType,
} from "@/modules/patients/services/__generated__/ProgressNoteAppointmentFragment.graphql"
import { useUpdateProgressNoteEpisodeOfCareMutation } from "@/modules/patients/services/useUpdateProgressNoteEpisodeOfCareMutation"

interface EpisodeOfCareOptionProps {
  country: Country
  progressNoteId: string
  episodeOfCareRef: EpisodeOfCareFragment$key
  selected?: boolean
  episodeOfCareId?: string | null
}

const EpisodeOfCareOption = ({
  country,
  progressNoteId,
  episodeOfCareRef,
  selected,
  episodeOfCareId,
}: EpisodeOfCareOptionProps) => {
  const translation = useTranslation()
  const episodeOfCare = useFragment(getEpisodeOfCareFragment(country), episodeOfCareRef)
  const setSelectedEpisodeOfCare = useEpisodeOfCareStoreContext((state) => state.setEpisodeOfCareFragmentReference)

  const { setValue } = useFormContext<AssignEpisodeOfCareFormType>()

  const handleSelect = useUpdateProgressNoteEpisodeOfCareMutation(country)
  return (
    <DropdownMenuItem
      className="h-auto p-0"
      data-testid="existed-episode-of-care-item"
      onSelect={() => {
        FaroInsightsWrapper.pushEvent("create_episode_of_care_select")
        handleSelect({ id: progressNoteId, episodeOfCareId: episodeOfCare.id }, episodeOfCareId, () => {
          setSelectedEpisodeOfCare(episodeOfCareRef)
          setValue("icdCode", episodeOfCare.icdDiagnosis.diagnosisCode, { shouldValidate: true })
        })
      }}>
      <div
        className={`grid w-full content-center rounded-md px-2 py-1 ${
          selected
            ? `bg-primary-surface text-on-primary-surface hover:bg-primary-surface-hovered`
            : `text-on-surface hover:bg-surface-hovered`
        }`}
        data-testid={episodeOfCare.icdDiagnosis.diagnosisCode}>
        <span className="truncate">{episodeOfCare.icdDiagnosis.text}</span>
        <span className="text-on-surface-subdued truncate text-xs font-normal tracking-wide">
          {formatEpisodeOfCareDuration(new Date(episodeOfCare.updatedAt), translation)}
        </span>
      </div>
    </DropdownMenuItem>
  )
}

interface EpisodeOfCareDropdownProps {
  progressNoteId: string
  episodeOfCare: EpisodeOfCareFragment$data
  country: Country
  channel: CommunicationChannel
  requestType: RequestType
  suggestedQuestionnaireDiagnosisCodes: ProgressNoteAppointmentFragment$data["suggestedQuestionnaireDiagnosisCodes"]
}

export const isPrescriptionRequestType = (requestType: RequestType) => requestType.startsWith("PRESCRIPTION")

const EpisodeOfCareDropdown = ({
  progressNoteId,
  episodeOfCare,
  country,
  channel,
  requestType,
  suggestedQuestionnaireDiagnosisCodes,
}: EpisodeOfCareDropdownProps) => {
  const { t } = useTranslation()

  const data = usePatientEpisodesOfCareData()

  const dataRef = data.patientEpisodesOfCare?.edges?.filter((r) => r.node.isUpdatable)
  const setAssignedEpisodeOfCareIdForTheCurrentProgressNote = useEpisodeOfCareStoreContext(
    (state) => state.setAssignedEpisodeOfCareIdForTheCurrentProgressNote,
  )
  const setProgressNoteId = useEpisodeOfCareStoreContext((state) => state.setProgressNoteId)

  const { setValue } = useFormContext<AssignEpisodeOfCareFormType>()

  const setAssignEpisodeOfCareForm = useEpisodeOfCareStoreContext((state) => state.setAssignEpisodeOfCareForm)

  const appointmentId = useAppointmentParam()

  const isDropdownOpen = useEpisodeOfCareStoreContext(
    (state) => state.progressNoteIdWithOpenEpisodeOfCareDropdown === progressNoteId,
  )
  const setProgressNoteIdWithOpenEpisodeOfCareDropdown = useEpisodeOfCareStoreContext(
    (state) => state.setProgressNoteIdWithOpenEpisodeOfCareDropdown,
  )
  const dropdownTriggerRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (isDropdownOpen) {
      dropdownTriggerRef.current?.scrollIntoView({ block: "nearest" })
    }
  }, [isDropdownOpen])

  return (
    <DropdownMenu
      modal={false}
      open={isDropdownOpen}
      onOpenChange={(open) => {
        setProgressNoteIdWithOpenEpisodeOfCareDropdown(open ? progressNoteId : null)
        if (open) {
          FaroInsightsWrapper.pushEvent("create_episode_of_care_edit")
        }
      }}>
      <DropdownMenuTrigger asChild ref={dropdownTriggerRef}>
        <Button
          variant="tertiary"
          className={cn(
            "h-auto truncate rounded-md border-none p-0 px-1 text-left focus-visible:outline-0",
            episodeOfCare.isVirtual && "text-critical",
          )}
          data-testid="assign-episode-of-care-btn">
          <span className="truncate text-base font-medium">
            {!episodeOfCare.isVirtual
              ? episodeOfCare.icdDiagnosis.text
              : t("episodeOfCare.progressNote.assignToEpisodeOfCare")}
          </span>
          <CaretDown size={20} className="text-on-surface-subdued shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="bg-surface shadow-4 absolute top-full left-0 z-10 box-border min-w-[288px] origin-top-left scale-100 transform rounded-xl focus-visible:outline-hidden"
        align="start">
        <ScrollArea viewportClassName="[&>div]:block!">
          <div className="max-h-75" data-testid="assign-episode-of-care-dropdown-dialog">
            <DropdownMenuItem
              className="p-2"
              onClick={() => {
                setAssignedEpisodeOfCareIdForTheCurrentProgressNote(episodeOfCare.id)
                setProgressNoteId(progressNoteId)
                setAssignEpisodeOfCareForm(setValue)
              }}>
              <div
                className="text-on-surface hover:bg-surface-hovered flex w-full items-center gap-4 rounded-md px-2 py-1"
                data-testid="new-episode-of-care-btn">
                <Plus className="text-on-surface-subdued" />
                <span className="truncate">{t("episodeOfCare.progressNote.newEpisodeOfCare")}</span>
              </div>
            </DropdownMenuItem>
            {dataRef && dataRef.filter((option) => !option.node.isVirtual).length > 0 && (
              <>
                <DropdownMenuSeparator className="border-border -mx-2 my-0 border-t" />
                <div className="grid gap-0.5 p-2">
                  {dataRef
                    .sort((a, b) => b.node.updatedAt.localeCompare(a.node.updatedAt))
                    .map((option) => {
                      if (!option.node.isVirtual) {
                        return (
                          <EpisodeOfCareOption
                            key={option.node.id}
                            progressNoteId={progressNoteId}
                            country={country}
                            episodeOfCareRef={option.node}
                            selected={option.node.id === episodeOfCare.id}
                            episodeOfCareId={episodeOfCare.id}
                          />
                        )
                      }
                    })}
                </div>
              </>
            )}
            <EpisodeOfCareSofiaSuggestions
              country={country}
              progressNoteId={progressNoteId}
              episodeOfCareId={episodeOfCare.id}
              appointmentId={appointmentId ?? undefined}
              questionnaireSuggestions={
                channel === "ASYNC_REQUEST" && isPrescriptionRequestType(requestType)
                  ? suggestedQuestionnaireDiagnosisCodes
                  : []
              }
            />
          </div>
        </ScrollArea>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export { EpisodeOfCareDropdown }
